import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
    Quest,
    QuestBomItem,
    QuestCheckResult,
    QuestDisplayType,
    QuestPartChange,
    QuestService,
    QuestValueChange,
} from '../../../../quest/src/public_api';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ConfigitQuestAdapterService } from '../configit-quest-adapter/configit-quest-adapter.service';
import { ConfigurationService } from '../services/configuration.service';
import { Router } from '@angular/router';
import { ConfigitBackend, EventService } from '../services/event.service';
import { ConfigitAceQuestAdapterService } from '../configit-ace-quest-adapter/configit-ace-quest-adapter.service';

interface QuestServiceWithDynamicDisplayType extends QuestService {
    changeDisplayType(type: QuestDisplayType): void;
}

@Component({
    selector: 'app-displaytypes',
    templateUrl: './displaytypes.component.html',
    styleUrls: ['./displaytypes.component.scss'],
})
export class DisplaytypesComponent implements OnInit {
    public quest: Subject<Quest> = new Subject<Quest>();
    public problem?: string;
    public bomItems: QuestBomItem[];

    public displayType: QuestDisplayType;
    public model: string;
    public lang: string;
    public application: string;
    public backend: ConfigitBackend = ConfigitBackend.model;

    public loading = true;

    /* skeleton themes */
    public skeleton = {
        label: {
            height: '32px',
            width: '60px',
        },
        card: {
            small: {
                height: '80px',
            },
            big: {
                height: '300px',
            },
        },
        input: {
            height: '40px',
        },
    };

    constructor(
        private quests: ConfigitQuestAdapterService,
        private ace: ConfigitAceQuestAdapterService,
        private snackbar: MatSnackBar,
        private translate: TranslateService,
        private configuration: ConfigurationService,
        private router: Router,
        private eventService: EventService
    ) {}

    private questService(): QuestServiceWithDynamicDisplayType {
        return this.backend === ConfigitBackend.ace ? this.ace : this.quests;
    }

    ngOnInit(): void {
        this.eventService.changeEmitted.subscribe((params) => {
            if (params.backend) {
                this.changeBackend(params.backend);
            }
            if (params.lang) {
                this.changeLang(params.lang);
            }
            if (params.model) {
                this.changeModel(params.model);
            }
            if (params.displayMode) {
                this.changeView(params.displayMode);
            }
            if (params.application) {
                this.changeApplication(params.application);
            }
        });

        const parameters = new URLSearchParams(window.location.search);

        this.backend = <ConfigitBackend>parameters.get('backend') || ConfigitBackend.model;
        this.lang = parameters.get('lang') || environment.defaultLanguage;
        this.application = parameters.get('application') || 'quote-assist';
        this.displayType =
            <QuestDisplayType>parameters.get('displayMode') || environment.quest.configurationDisplayType;
        this.model = parameters.get('model') || environment.quest.defaultModel;

        this.translate.setDefaultLang(this.lang);
        this.configuration.lang = this.lang;
        this.configuration.application = this.application;

        this.questService().changeDisplayType(this.displayType);

        this.loadModel();
        this.updateQueryParametersInUrl();
    }

    private updateQueryParametersInUrl() {
        this.router.navigate([], {
            queryParams: {
                backend: this.backend,
                lang: this.lang,
                model: this.model,
                application: this.application,
                displayMode: this.displayType,
            },
            queryParamsHandling: 'merge',
        });
    }

    private loadModel() {
        this.questService()
            .get(this.model)
            .subscribe({
                next: (model: Quest) => this.setModel(model),
                error: () => {
                    this.onError(`Could not find model ${this.model}`);
                    this.loading = false;
                },
            });
    }

    changeView(displayType: string) {
        this.loading = true;

        switch (displayType) {
            case 'single':
                this.displayType = QuestDisplayType.single;
                this.questService().changeDisplayType(QuestDisplayType.single);
                break;
            case 'stepped':
                this.displayType = QuestDisplayType.stepped;
                this.questService().changeDisplayType(QuestDisplayType.stepped);
                break;
            case 'tabbed':
                this.displayType = QuestDisplayType.tabbed;
                this.questService().changeDisplayType(QuestDisplayType.tabbed);
                break;
            case 'wizard':
                this.displayType = QuestDisplayType.wizard;
                this.questService().changeDisplayType(QuestDisplayType.wizard);
                break;
        }

        this.updateQueryParametersInUrl();
        this.loadModel();
    }

    changeBackend(backend: ConfigitBackend) {
        this.loading = true;
        this.backend = backend;
        this.updateQueryParametersInUrl();
        this.loadModel();
    }

    changeLang(lang: string) {
        this.lang = lang;
        this.configuration.lang = this.lang;
        this.translate.setDefaultLang(lang);

        this.updateQueryParametersInUrl();
        this.loadModel();
    }

    changeApplication(application: string) {
        this.loading = true;
        this.application = application;
        this.configuration.application = application;
        this.updateQueryParametersInUrl();
        this.loadModel();
    }

    changeModel(modelId: string) {
        this.loading = true;

        this.model = modelId;
        this.questService()
            .get(modelId)
            .subscribe({
                next: (model: Quest) => {
                    this.setModel(model);

                    this.updateQueryParametersInUrl();
                },
                error: () => {
                    this.onError(`Could not find model ${modelId}`);
                    this.loading = false;
                },
            });
    }

    private setModel(model: Quest) {
        this.loading = false;
        this.quest.next(model);
    }

    check(changed: QuestValueChange) {
        this.questService()
            .check(changed)
            .subscribe({
                next: (result: QuestCheckResult) => {
                    if (result) {
                        this.setModel(result.model);
                        this.bomItems = result.bomItems;
                    } else {
                        throw new Error('missing model');
                    }
                },
                error: (error) => {
                    this.onError('Unknown error occurred');
                    console.log(error);
                },
            });
    }

    page(changed: QuestPartChange) {
        // TODO: do something here
        console.log('next/back was pressed!');
    }

    submit(model: any) {
        // TODO: decide what to do here
        console.log('SUBMIT!');
    }

    private onError(errorMsg: string) {
        // TODO: decide what to do with errors
        this.snackbar.open(errorMsg, '', {
            panelClass: ['error-snackbar'],
            duration: 3000,
        });
    }
}
